// Shared store for the notification + chat-unread badges.
//
// Previously BOTH the Navbar and the DynamicSidebar polled `/chat/unread-count`
// (and the Navbar also `/notifications`) every 20s — so the app fired 3 badge
// requests every 20s (~9/min) with two of them identical. This module polls each
// endpoint ONCE on a single 60s interval and fans the result out to every
// subscriber, so the whole app makes 2 badge requests/min regardless of how many
// components show a badge.
//
// It is a plain module-level store (no Context provider needed): components
// subscribe via the `useBadges()` hook. The interval only runs while at least one
// subscriber is mounted (ref-counted), and stops when the last one unmounts.

import { useState, useEffect } from 'react';
import API from './api';

const POLL_MS = 60 * 1000; // was 20s in two places; one shared 60s poll now

let state = {
  unreadChatCount: 0,
  notifications: [],      // full list (consumers slice as needed)
  notificationCount: 0,   // unread count
};

const subscribers = new Set();
let intervalId = null;
let refCount = 0;

const emit = () => {
  subscribers.forEach((fn) => {
    try { fn(state); } catch (e) { /* ignore a bad subscriber */ }
  });
};

export const fetchChatUnread = async () => {
  try {
    const res = await API.get('/chat/unread-count');
    state = { ...state, unreadChatCount: res.data?.data?.unreadCount || 0 };
    emit();
  } catch (e) { /* silent — keep last known value */ }
};

export const fetchNotifications = async () => {
  try {
    const res = await API.get('/notifications');
    const list = Array.isArray(res.data) ? res.data : [];
    state = {
      ...state,
      notifications: list,
      notificationCount: list.filter((n) => !n.isRead).length,
    };
    emit();
  } catch (e) { /* silent — keep last known value */ }
};

const refreshAll = () => {
  fetchChatUnread();
  fetchNotifications();
};

/** Force an immediate refresh (e.g. after an action that changes the badges). */
export const refreshBadges = () => refreshAll();

export const getBadgeState = () => state;

/**
 * Locally override the unread notification count without an extra request
 * (e.g. right after marking all notifications as read).
 */
export const setNotificationCount = (n) => {
  state = { ...state, notificationCount: n };
  emit();
};

/** Low-level subscription. Returns an unsubscribe fn. */
export const subscribeBadges = (fn) => {
  subscribers.add(fn);
  fn(state); // push current snapshot immediately
  refCount += 1;
  if (refCount === 1) {
    refreshAll();                              // initial load
    intervalId = setInterval(refreshAll, POLL_MS);
  }
  return () => {
    subscribers.delete(fn);
    refCount -= 1;
    if (refCount <= 0 && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
      refCount = 0;
    }
  };
};

/** React hook: returns { unreadChatCount, notifications, notificationCount }. */
export const useBadges = () => {
  const [snapshot, setSnapshot] = useState(getBadgeState);
  useEffect(() => subscribeBadges(setSnapshot), []);
  return snapshot;
};
